import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Modal,
  TextField,
  Chip,
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../common/components/dashboard_layout.component';

// Reuse existing sections + functions from VendorInvoiceManagement
import POSection from '../VendorInvoiceManagement/components/po_section.component';
import AdditionalInfoSection from '../VendorInvoiceManagement/components/additional_info_section.component';
import FileUploadSection from '../VendorInvoiceManagement/components/file_upload_section.component';
import MultipleFileUploadSection from '../VendorInvoiceManagement/components/multiple_file_upload_section.component';
import SystemInvoiceNumberSection from './components/SystemInvoiceNumberSection';
import SystemPoSiteExcelUploadSection from './components/SystemPoSiteExcelUploadSection';
import { parsePDFFile } from '../VendorInvoiceManagement/functions/parsePDF.function';
import { notify } from '../../common/functions/react-toastify.function';
import Swal from 'sweetalert2';
import {
  getDisallowedFilenameCharsMessage,
  hasDisallowedFilenameChars,
} from '../../common/functions/file_name_validation.function';
import { createSystemVendorInvoice } from './functions/create_system_vendor_invoice.function';
import { verifyPO } from '../VendorInvoiceManagement/functions/verify_po.function';
import { getPoRingsDetail } from '../VendorInvoiceManagement/functions/get_po_rings_detail.function';
import { getSystemPoAuthorizedRecordByPoWithItemsAndClaims } from '../SystemPurchaseOrderManagement/functions/get_system_po_authorized_record_by_po.function';

const SystemVendorInvoiceForm = () => {
  const navigate = useNavigate();

  const permissionNames = useMemo(() => {
    try {
      const stored = localStorage.getItem('permissions');
      if (!stored) {
        return [];
      }
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed.map((p) => p.name) : [];
    } catch (error) {
      console.error('Failed to parse permissions:', error);
      return [];
    }
  }, []);

  useEffect(() => {
    if (!permissionNames.includes('System Invoice Management Vendor')) {
      navigate('/rov', { replace: true });
    }
  }, [permissionNames, navigate]);

  const [isParsingPDF, setIsParsingPDF] = useState(false);
  const [isVerifyingPO, setIsVerifyingPO] = useState(false);
  const [isFetchingSystemPo, setIsFetchingSystemPo] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [poVerificationData, setPoVerificationData] = useState(null);
  const [ringDetailsData, setRingDetailsData] = useState([]);
  const [poUnitPrice, setPoUnitPrice] = useState('');
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');
  const [formData, setFormData] = useState({
    vendor: '',
    poNumber: '',
    poAmount: '',
    poDate: '',
    deliverDate: '',
    poFile: null,
    totalClaimedAmount: '',
    invoiceNumber: '',
    invoiceNumberVendor: '',
    invoiceText: '',
    invoiceAmount: '',
    invoiceType: 'FTTS',
    type: '',
    poType: '',
    rings: [
      {
        ringName: '',
        sectionName: '',
        category: '',
        paymentRingMilestone: '',
        unitRate: '',
        length: '',
        amount: '',
      },
    ],
    region: '',
    jazzRORegionalPOC: '',
    submittersEmail: '',
    siteIds: '',
    atpStatus: '',
    pacStatus: '',
    facStatus: '',
    nocRowIssuanceDate: '',
    completionPacDate: '',
    remainingPOAmount: '',
    vendorRemarks: '',
    otherDocuments: [],
  });

  const [serviceItems, setServiceItems] = useState([]);
  const [uploadedSites, setUploadedSites] = useState([]);
  const [existingInvoiceSites, setExistingInvoiceSites] = useState([]);
  const [poClaimedAmount, setPoClaimedAmount] = useState(0);

  const parseMoney = (v) => {
    const n = Number(
      String(v ?? '')
        .replace(/,/g, '')
        .trim()
    );
    return Number.isFinite(n) ? n : 0;
  };

  const roundTo = (value, decimals = 2) => {
    const n = Number(value);
    if (!Number.isFinite(n)) {
      return 0;
    }
    const factor = 10 ** decimals;
    return Math.round((n + Number.EPSILON) * factor) / factor;
  };

  const roundMoney = (value) => roundTo(value, 2);
  const roundQty = (value) => roundTo(value, 6);

  const formatMoney = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) {
      return '0.00';
    }
    return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getItemInvoiceMetrics = (item) => {
    const poQty = roundQty(parseMoney(item?.poQty));
    const claimedQty = roundQty(parseMoney(item?.claimedQty));
    const maxInvoiceQty = roundQty(Math.max(0, poQty - claimedQty));
    const enteredInvoicedQty = roundQty(parseMoney(item?.invoicedQty));
    const effectiveInvoicedQty = roundQty(Math.min(enteredInvoicedQty, maxInvoiceQty));
    const unitPrice = parseMoney(item?._raw?.unit_price);
    const amount = roundMoney(effectiveInvoicedQty * unitPrice);
    const remainingQty = roundQty(Math.max(0, poQty - claimedQty - effectiveInvoicedQty));

    return {
      poQty,
      claimedQty,
      maxInvoiceQty,
      effectiveInvoicedQty,
      unitPrice,
      amount,
      remainingQty,
    };
  };

  const formatDateOnly = (value) => {
    if (!value) {
      return '';
    }
    return String(value).split('T')[0];
  };

  const siteChipColors = [
    { backgroundColor: '#E3F2FD', color: '#0D47A1' },
    { backgroundColor: '#E8F5E9', color: '#1B5E20' },
    { backgroundColor: '#FFF3E0', color: '#E65100' },
    { backgroundColor: '#F3E5F5', color: '#4A148C' },
    { backgroundColor: '#E0F2F1', color: '#004D40' },
    { backgroundColor: '#FCE4EC', color: '#880E4F' },
  ];

  const normalizeItemCode = (value) => {
    if (value === null || value === undefined) {
      return '';
    }
    let s = String(value).trim().replace(/\s+/g, '');
    if (/^\d+(\.0+)?$/.test(s)) {
      s = String(parseInt(s, 10));
    }
    return s;
  };

  const totalInvoiceAmount = useMemo(() => {
    return (serviceItems || []).reduce((sum, r) => {
      const { amount } = getItemInvoiceMetrics(r);
      return roundMoney(sum + amount);
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceItems]);
  const isTotalInvoiceAmountZero = totalInvoiceAmount <= 0;
  const isRegionSelected = Boolean(String(formData.region || '').trim());
  const isInvoiceNumberComplete = useMemo(() => {
    const vendor = String(formData.invoiceNumberVendor || '').trim();
    const pti = String(formData.invoiceText || '').trim();
    const invoiceNumber = String(formData.invoiceNumber || '').trim();
    if (!vendor || !pti || !invoiceNumber) {
      return false;
    }
    const date = new Date().toISOString().slice(0, 10);
    const expected = `${vendor}-${date}-${pti}`;
    return invoiceNumber === expected;
  }, [formData.invoiceNumberVendor, formData.invoiceText, formData.invoiceNumber]);
  const hasInsertedSites = (uploadedSites || []).some((s) => String(s || '').trim());
  const isSubmitDisabled =
    isSubmitting ||
    isTotalInvoiceAmountZero ||
    !isRegionSelected ||
    !isInvoiceNumberComplete ||
    !hasInsertedSites;

  useEffect(() => {
    // Keep invoiceAmount derived from the invoiced quantities (no manual mismatch).
    setFormData((prev) => ({
      ...prev,
      invoiceAmount: totalInvoiceAmount ? String(totalInvoiceAmount.toFixed(2)) : '',
    }));
  }, [totalInvoiceAmount]);

  useEffect(() => {
    // Remaining PO Amount = Total PO Amount - (Already claimed amount + current invoice amount)
    const poAmt = parseMoney(formData?.poAmount);
    const invAmt = Number.isFinite(totalInvoiceAmount) ? totalInvoiceAmount : 0;
    const claimedAmt = Number.isFinite(poClaimedAmount) ? poClaimedAmount : 0;
    const remaining = roundMoney(Math.max(0, poAmt - (claimedAmt + invAmt)));
    setFormData((prev) => ({
      ...prev,
      remainingPOAmount: poAmt ? String(remaining.toFixed(2)) : prev.remainingPOAmount || '',
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.poAmount, totalInvoiceAmount, poClaimedAmount]);

  const handleBack = () => {
    navigate('/systemInvoiceManagementVendor');
  };

  const handleInputChange = (_, field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleRemoveRing = (index) => {
    setFormData((prev) => ({
      ...prev,
      rings: (prev.rings || []).filter((_, i) => i !== index),
    }));
  };

  const handleRingChange = (index, field, value) => {
    setFormData((prev) => {
      const newRings = [...prev.rings];
      newRings[index] = { ...newRings[index], [field]: value };
      return { ...prev, rings: newRings };
    });
  };

  const amountMismatchError = null;

  const showErrorModal = (message) => {
    setErrorModalMessage(message);
    setErrorModalOpen(true);
  };

  const getSystemPoHydration = async (poNumber) => {
    const poNo = String(poNumber || '').trim();
    if (!poNo) {
      return null;
    }

    const poRes = await getSystemPoAuthorizedRecordByPoWithItemsAndClaims(poNo);
    if (!poRes.success) {
      throw new Error(poRes.error || 'Failed to fetch PO from backend');
    }
    const payload = poRes.data || {};
    const poRow = payload.po || {};
    const claimedByItemCode = payload.claimedByItemCode || {};
    const claimedByItemId = payload.claimedByItemId || {};
    const claimedAmount = Number(payload.claimedAmount || 0) || 0;

    const items = Array.isArray(payload.items) ? payload.items : [];
    const serviceItemsData = items.map((it, index) => {
      const name =
        String(it.detailed_description || '').trim() || String(it.item_code || '').trim() || 'Item';
      const poQtyNum = Number(String(it.quantity || '').replace(/,/g, ''));
      const itemCodeKey = normalizeItemCode(it.item_code);
      const rowId = it?.id != null ? it.id : index;
      // Prefer per-row claimed quantity (handles duplicate item codes independently);
      // fall back to the item_code aggregate for older payloads.
      const claimedQtyFromInvoices =
        claimedByItemId && Object.prototype.hasOwnProperty.call(claimedByItemId, rowId)
          ? Number(claimedByItemId[rowId] || 0) || 0
          : Number(claimedByItemCode?.[itemCodeKey] || 0) || 0;
      return {
        name,
        poQty: Number.isFinite(poQtyNum) ? poQtyNum : 0,
        claimedQty: Math.max(0, claimedQtyFromInvoices),
        invoicedQty: '',
        _raw: it,
      };
    });

    return {
      claimedAmount,
      serviceItemsData,
      existingInvoiceSites: Array.isArray(payload.existingInvoiceSites)
        ? payload.existingInvoiceSites
        : [],
      formFields: {
        vendor: poRow.vendor || '',
        poNumber: poRow.po || poNo,
        poAmount: poRow.total_amount != null ? String(poRow.total_amount) : '',
        poDate: formatDateOnly(poRow.po_date),
        deliverDate: formatDateOnly(poRow.po_delivery_date),
        totalClaimedAmount: claimedAmount ? String(claimedAmount.toFixed(2)) : '',
        poType: poRow.type || '',
        type: poRow.type || '',
      },
    };
  };

  const handleVerifyPO = async () => {
    setIsVerifyingPO(true);
    try {
      const poNumber = formData.poNumber;
      const res = await verifyPO(poNumber);
      setPoVerificationData(res?.data || res);
      const rings = await getPoRingsDetail(poNumber);
      const ringsData = rings?.data || rings || [];
      setRingDetailsData(ringsData);
      setPoUnitPrice(res?.data?.unitRate || '');
      notify('success', 'PO verified successfully');
    } catch (err) {
      showErrorModal(err?.message || 'Failed to verify PO');
    } finally {
      setIsVerifyingPO(false);
    }
  };

  const handlePoFileChange = async (file) => {
    // FileUploadSection already validates PDF; still guard.
    if (!file) {
      return;
    }
    if (hasDisallowedFilenameChars(file.name)) {
      Swal.fire({
        title: 'Invalid file name',
        text: `This file name contains unsupported characters. ${getDisallowedFilenameCharsMessage()}`,
        icon: 'error',
        confirmButtonColor: '#BD1D23',
        customClass: { popup: 'small-swal' },
      });
      return;
    }
    setFormData((prev) => ({ ...prev, poFile: file }));
    setIsParsingPDF(true);
    setIsFetchingSystemPo(false);
    try {
      const parsed = await parsePDFFile(file);
      const poNo = parsed?.poNumber || formData.poNumber;
      let systemPoHydration = null;

      if (poNo) {
        setIsFetchingSystemPo(true);
        systemPoHydration = await getSystemPoHydration(poNo);
      } else {
        setExistingInvoiceSites([]);
        setUploadedSites([]);
      }

      const systemPoFields = systemPoHydration?.formFields || {};
      setPoClaimedAmount(systemPoHydration?.claimedAmount || 0);
      setServiceItems(systemPoHydration?.serviceItemsData || []);
      setExistingInvoiceSites(systemPoHydration?.existingInvoiceSites || []);
      setUploadedSites([]);
      setFormData((prev) => ({
        ...prev,
        ...parsed,
        vendor: systemPoFields.vendor || parsed.vendor || prev.vendor || '',
        poNumber: systemPoFields.poNumber || parsed.poNumber || prev.poNumber || '',
        poAmount: systemPoFields.poAmount || parsed.poAmount || prev.poAmount || '',
        poDate: formatDateOnly(systemPoFields.poDate || parsed.poDate || prev.poDate),
        deliverDate: formatDateOnly(
          systemPoFields.deliverDate || parsed.deliverDate || prev.deliverDate
        ),
        totalClaimedAmount:
          systemPoFields.totalClaimedAmount || parsed.totalClaimedAmount || prev.totalClaimedAmount,
        poType: systemPoFields.poType || prev.poType || '',
        type: systemPoFields.type || prev.type || '',
        poFile: file,
      }));
    } catch (err) {
      showErrorModal(err?.message || 'Failed to parse PDF');
    } finally {
      setIsParsingPDF(false);
      setIsFetchingSystemPo(false);
    }
  };

  const handleSubmit = async () => {
    if (isTotalInvoiceAmountZero) {
      showErrorModal('Total Invoice Amount must be greater than zero before submitting.');
      return;
    }
    if (!isRegionSelected) {
      showErrorModal('Please select Region before submitting.');
      return;
    }
    if (!isInvoiceNumberComplete) {
      showErrorModal(
        'Please select Vendor, enter PTI Number, and complete the full invoice number (Vendor - YYYY-MM-DD - PTI Number) before submitting.'
      );
      return;
    }
    if (!hasInsertedSites) {
      showErrorModal('Please insert sites for creating invoice.');
      return;
    }

    setIsSubmitting(true);
    try {
      const normalizedSites = Array.from(
        new Set((uploadedSites || []).map((s) => String(s || '').trim()).filter(Boolean))
      );
      const serviceItemsData = (serviceItems || []).map((r) => {
        const { claimedQty, effectiveInvoicedQty, remainingQty } = getItemInvoiceMetrics(r);
        return {
          name: r.name,
          po_qty: Number(r.poQty) || 0,
          claimed_qty: claimedQty,
          invoiced_qty: effectiveInvoicedQty,
          remaining_qty: remainingQty,
          item_code: r?._raw?.item_code ?? null,
          item_type: r?._raw?.item_type ?? null,
          um: r?._raw?.um ?? null,
          unit_price: r?._raw?.unit_price ?? null,
          detailed_description: r?._raw?.detailed_description ?? null,
        };
      });

      const resolvedType = String(formData.poType || formData.type || '').trim();
      const res = await createSystemVendorInvoice({
        formData: {
          ...formData,
          type: resolvedType,
          poType: resolvedType,
        },
        serviceItemsData,
        sites: normalizedSites,
      });
      if (res) {
        notify('success', 'Invoice created successfully');
        navigate('/systemInvoiceManagementVendor');
      }
    } catch (err) {
      showErrorModal(err?.message || 'Failed to create invoice');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DashboardLayout padding={true}>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <IconButton onClick={handleBack}>
            <ArrowBackIosIcon />
          </IconButton>
          <Typography variant="h6">New System Invoice</Typography>
        </Box>

        <FileUploadSection
          title="PO File"
          description="Click or drag file to Fetch from PO"
          downloadLinkText=""
          onFileChange={handlePoFileChange}
          file={formData.poFile}
          isLoading={isParsingPDF || isFetchingSystemPo}
          loadingText="Fetching PO details..."
        />

        <POSection
          formData={formData}
          onInputChange={handleInputChange}
          poVerificationData={poVerificationData}
        />
        <SystemPoSiteExcelUploadSection
          currentPoNumber={formData.poNumber}
          existingInvoiceSites={existingInvoiceSites}
          onUploaded={({ poNumber, insertedSites }) => {
            // Keep selected sites tied to the PO currently loaded on this invoice.
            if (poNumber && String(poNumber) !== String(formData.poNumber || '').trim()) {
              return;
            }
            setUploadedSites((prev) => {
              const next = [...(prev || []), ...(insertedSites || [])];
              return Array.from(new Set(next.map((s) => String(s || '').trim()).filter(Boolean)));
            });
          }}
        />

        {uploadedSites.length > 0 && (
          <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {uploadedSites.map((s, index) => {
              const chipColor = siteChipColors[index % siteChipColors.length];
              return (
                <Chip
                  key={s}
                  size="small"
                  label={s}
                  sx={{
                    ...chipColor,
                    borderRadius: '10px',
                    fontWeight: 600,
                  }}
                />
              );
            })}
          </Box>
        )}

        <SystemInvoiceNumberSection
          vendor={formData.invoiceNumberVendor}
          ptiNumber={formData.invoiceText}
          invoiceNumber={formData.invoiceNumber}
          onChange={({ vendor, ptiNumber }) =>
            setFormData((prev) => {
              const date = new Date().toISOString().slice(0, 10);
              const v = String(vendor || '').trim();
              const p = String(ptiNumber || '').trim();
              const invoiceNumber = v && p ? `${v}-${date}-${p}` : '';
              return { ...prev, invoiceNumberVendor: v, invoiceText: p, invoiceNumber };
            })
          }
        />

        {/* Dummy table under Invoice Number */}
        <Box sx={{ mb: 4 }}>
          <Typography
            sx={{
              fontSize: '16px',
              fontFamily: 'Inter',
              fontWeight: 600,
              mb: 1.5,
              display: 'flex',
              color: '#192126',
            }}
          >
            Services / Items
          </Typography>

          {Array.isArray(serviceItems) && serviceItems.length > 0 ? (
            <>
              <Box
                component="table"
                sx={{
                  width: '100%',
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                  backgroundColor: '#fff',
                  border: '1px solid #EAECF0',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0px 2px 10px rgba(16, 24, 40, 0.06)',
                  '& th': {
                    textAlign: 'left',
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#344054',
                    background:
                      'linear-gradient(180deg, rgba(248, 232, 233, 0.65) 0%, rgba(249, 250, 251, 1) 100%)',
                    padding: '8px 10px',
                    borderBottom: '1px solid #EAECF0',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                  },
                  '& td': {
                    fontSize: '11px',
                    color: '#667085',
                    padding: '6px 10px',
                    borderBottom: '1px solid #F2F4F7',
                    lineHeight: 1.2,
                  },
                  '& tbody tr:nth-of-type(odd) td': {
                    backgroundColor: '#FCFCFD',
                  },
                  '& tbody tr:hover td': {
                    backgroundColor: 'rgba(189, 29, 35, 0.06)',
                  },
                  '& tr:last-child td': { borderBottom: 'none' },
                }}
              >
                <thead>
                  <tr>
                    <th>Item Code</th>
                    <th>Service/Item</th>
                    <th>Item Type</th>
                    <th style={{ textAlign: 'center' }}>U/M</th>
                    <th style={{ textAlign: 'right' }}>Unit Price</th>
                    <th style={{ textAlign: 'right' }}>PO Quantity</th>
                    <th style={{ textAlign: 'right' }}>Claimed Qty</th>
                    <th style={{ textAlign: 'right' }}>Invoiced Qty</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                    <th style={{ textAlign: 'right' }}>Remaining Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {serviceItems.map((row, idx) => {
                    const { poQty, claimedQty, maxInvoiceQty, amount, remainingQty } =
                      getItemInvoiceMetrics(row);
                    const itemCode = String(row?._raw?.item_code || '').trim();
                    const itemType = String(row?._raw?.item_type || '').trim();
                    const um = String(row?._raw?.um || '').trim();
                    const unitPrice = String(row?._raw?.unit_price || '').trim();

                    return (
                      <tr key={`${row.name}-${idx}`}>
                        <td style={{ fontWeight: 700, color: '#101828' }}>{itemCode || '—'}</td>
                        <td style={{ fontWeight: 600, color: '#101828' }}>{row.name}</td>
                        <td>{itemType || '—'}</td>
                        <td style={{ textAlign: 'center' }}>{um || '—'}</td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {unitPrice || '—'}
                        </td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {poQty}
                        </td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {claimedQty}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <TextField
                            size="small"
                            value={row.invoicedQty}
                            helperText={maxInvoiceQty >= 0 ? `Max ${maxInvoiceQty}` : ' '}
                            onChange={(e) => {
                              const raw = e.target.value;
                              // allow empty while typing
                              if (raw === '') {
                                setServiceItems((prev) =>
                                  prev.map((r, i) => (i === idx ? { ...r, invoicedQty: '' } : r))
                                );
                                return;
                              }
                              const cleaned = raw.replace(/[^\d.]/g, '');
                              const nextNum = Number(cleaned);
                              const clamped =
                                Number.isFinite(nextNum) && nextNum > maxInvoiceQty
                                  ? String(maxInvoiceQty)
                                  : cleaned;
                              setServiceItems((prev) =>
                                prev.map((r, i) => (i === idx ? { ...r, invoicedQty: clamped } : r))
                              );
                            }}
                            inputProps={{
                              inputMode: 'numeric',
                              style: {
                                textAlign: 'right',
                                fontVariantNumeric: 'tabular-nums',
                                paddingTop: 4,
                                paddingBottom: 4,
                              },
                            }}
                            sx={{
                              minWidth: 110,
                              '& .MuiFormHelperText-root': {
                                margin: 0,
                                mt: 0.25,
                                textAlign: 'right',
                                fontSize: '10px',
                                lineHeight: 1.1,
                              },
                              '& .MuiInputBase-input': { fontSize: '11px' },
                              '& .MuiOutlinedInput-root': { backgroundColor: '#fff' },
                            }}
                          />
                        </td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {amount ? formatMoney(amount) : '—'}
                        </td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {remainingQty}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Box>

              {/* Summary interface like provided screenshot */}
              <Box
                component="table"
                sx={{
                  width: '100%',
                  mt: 2,
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                  backgroundColor: '#fff',
                  border: '1px solid #EAECF0',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0px 2px 10px rgba(16, 24, 40, 0.06)',
                  '& td': {
                    fontSize: '11px',
                    padding: '8px 10px',
                    borderBottom: '1px solid #F2F4F7',
                    verticalAlign: 'middle',
                  },
                  '& tr:last-child td': { borderBottom: 'none' },
                }}
              >
                <tbody>
                  {(() => {
                    const poList = serviceItems
                      .map((r) => getItemInvoiceMetrics(r).poQty)
                      .join(' / ');
                    const claimedList = serviceItems
                      .map((r) => getItemInvoiceMetrics(r).claimedQty)
                      .join(' / ');
                    const invoicedList = serviceItems
                      .map((r) => getItemInvoiceMetrics(r).effectiveInvoicedQty)
                      .join(' / ');
                    const remainingList = serviceItems
                      .map((r) => getItemInvoiceMetrics(r).remainingQty)
                      .join(' / ');
                    const totalAmountText = formatMoney(totalInvoiceAmount);
                    const claimedAmountText = formatMoney(poClaimedAmount);
                    const remainingPoAmountText = formatMoney(
                      roundMoney(
                        Math.max(
                          0,
                          parseMoney(formData?.poAmount) - (poClaimedAmount + totalInvoiceAmount)
                        )
                      )
                    );

                    const labelCellSx = (highlight = false) => ({
                      width: { xs: '45%', sm: 280 },
                      fontWeight: 800,
                      fontSize: '14px',
                      lineHeight: 1.15,
                      color: '#101828',
                      backgroundColor: highlight ? 'rgba(33, 150, 243, 0.18)' : '#fff',
                      borderRight: '1px solid #EAECF0',
                    });
                    const valueCellSx = {
                      fontWeight: 700,
                      fontSize: '14px',
                      color: '#101828',
                      fontVariantNumeric: 'tabular-nums',
                    };

                    return (
                      <>
                        <tr>
                          <td style={labelCellSx(true)}>PO Quantity</td>
                          <td style={valueCellSx}>{poList}</td>
                        </tr>
                        <tr>
                          <td style={labelCellSx(false)}>Already Claimed Quantity</td>
                          <td style={valueCellSx}>{claimedList}</td>
                        </tr>
                        <tr>
                          <td style={labelCellSx(false)}>Invoiced Quantity</td>
                          <td style={valueCellSx}>{invoicedList}</td>
                        </tr>
                        <tr>
                          <td style={labelCellSx(false)}>Remaining Quantity</td>
                          <td style={valueCellSx}>{remainingList}</td>
                        </tr>
                        <tr>
                          <td style={labelCellSx(false)}>Total Claimed Amount</td>
                          <td style={valueCellSx}>PKR {claimedAmountText}</td>
                        </tr>
                        <tr>
                          <td style={labelCellSx(true)}>Total Invoice Amount</td>
                          <td
                            style={{
                              ...valueCellSx,
                              color: totalInvoiceAmount > 0 ? '#101828' : '#BD1D23',
                            }}
                          >
                            PKR {totalAmountText}
                          </td>
                        </tr>
                        <tr>
                          <td style={labelCellSx(true)}>Remaining PO Amount</td>
                          <td style={valueCellSx}>PKR {remainingPoAmountText}</td>
                        </tr>
                      </>
                    );
                  })()}
                </tbody>
              </Box>
            </>
          ) : (
            <Typography sx={{ fontSize: 13, color: '#667085' }}>
              Upload a PO to load Services / Items.
            </Typography>
          )}
        </Box>

        <AdditionalInfoSection formData={formData} onInputChange={handleInputChange} />

        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>Vendor Remarks</Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            placeholder="Add any remarks from vendor..."
            value={formData.vendorRemarks || ''}
            onChange={(e) => handleInputChange('additional', 'vendorRemarks', e.target.value)}
          />
        </Box>

        <MultipleFileUploadSection
          description="Upload any additional supporting documents"
          downloadLinkText=""
          files={formData.otherDocuments || []}
          onFilesChange={(files) => setFormData((prev) => ({ ...prev, otherDocuments: files }))}
          onFileRemove={(index) =>
            setFormData((prev) => ({
              ...prev,
              otherDocuments: (prev.otherDocuments || []).filter((_, i) => i !== index),
            }))
          }
        />

        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={handleBack} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            sx={{ backgroundColor: '#BD1D23' }}
            startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Submit
          </Button>
        </Box>

        <Modal open={errorModalOpen} onClose={() => setErrorModalOpen(false)}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 500,
              bgcolor: 'background.paper',
              p: 3,
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" sx={{ mb: 1 }}>
              Error
            </Typography>
            <TextField fullWidth multiline minRows={3} value={errorModalMessage} />
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={() => setErrorModalOpen(false)}>Close</Button>
            </Box>
          </Box>
        </Modal>
      </Box>
    </DashboardLayout>
  );
};

export default SystemVendorInvoiceForm;
